/**
 * Admin Limits Contracts - Database-First Zod Schemas
 *
 * SOURCE OF TRUTH: PostgreSQL tables and stored procedures
 * Generated from MCP queries on 2026-01-02
 *
 * Related DB objects:
 * - Table: payment_services (id, hash, name, type, turned_off, restarted)
 * - Table: payment_service_config (payment_service_id, min_amount, max_amount)
 * - Function: update_payment_service_limits(p_hash, p_min_amount, p_max_amount, p_staff_id)
 *   Returns: TABLE(success boolean, message text, service_id integer)
 *
 * Note: Limits are stored per payment_service in payment_service_config (append-only).
 * Active status is computed from: turned_off IS NULL OR (restarted IS NOT NULL AND restarted > turned_off)
 */
import { z } from 'zod';
// Note: paymentServiceTypeSchema imported but not used directly in this file
// Keeping import for future use if needed
// =============================================================================
// METHOD LIMITS SCHEMA
// =============================================================================
/**
 * Payment method with limits
 * Returned by all limits endpoints
 */
export const methodLimitsSchema = z.object({
    method: z.string(), // hash (identifier)
    displayName: z.string(), // name (human readable)
    enabled: z.boolean(), // active status (computed)
    minAmount: z.number().int().min(0),
    maxAmount: z.number().int().min(0),
});
// =============================================================================
// RESPONSE DATA TYPES (for frontend - inner data without success wrapper)
// =============================================================================
/**
 * All limits response data (inner data)
 * Used by frontend after extracting from API response
 */
export const allLimitsResponseDataSchema = z.object({
    deposit: z.array(methodLimitsSchema),
    withdrawal: z.array(methodLimitsSchema),
});
/**
 * Single type limits response data (inner data)
 */
export const limitsListResponseDataSchema = z.object({
    limits: z.array(methodLimitsSchema),
});
// =============================================================================
// GET LIMITS ENDPOINTS
// =============================================================================
/**
 * GET /api/admin/limits response
 * Returns all limits (deposit + withdrawal)
 */
export const getAllLimitsResponseSchema = z.object({
    success: z.literal(true),
    data: allLimitsResponseDataSchema,
});
/**
 * GET /api/admin/limits/deposit response
 * Returns all deposit method limits
 */
export const getDepositLimitsResponseSchema = z.object({
    success: z.literal(true),
    data: limitsListResponseDataSchema,
});
/**
 * GET /api/admin/limits/withdrawal response
 * Returns all withdrawal method limits
 */
export const getWithdrawalLimitsResponseSchema = z.object({
    success: z.literal(true),
    data: limitsListResponseDataSchema,
});
/**
 * GET /api/admin/limits/deposit/:method response
 * GET /api/admin/limits/withdrawal/:method response
 * Returns single method limits
 */
export const getMethodLimitsResponseSchema = z.object({
    success: z.literal(true),
    data: methodLimitsSchema,
});
// =============================================================================
// UPDATE LIMITS ENDPOINTS
// =============================================================================
/**
 * PUT /api/admin/limits/deposit/:method request
 * PUT /api/admin/limits/withdrawal/:method request
 *
 * Both fields are optional - current value is preserved if not provided
 */
export const updateMethodLimitsRequestSchema = z.object({
    minAmount: z.number().int().min(0).optional(),
    maxAmount: z.number().int().min(0).optional(),
}).refine((data) => {
    // If both provided, validate min <= max
    if (data.minAmount !== undefined && data.maxAmount !== undefined) {
        return data.minAmount <= data.maxAmount;
    }
    return true;
}, { message: 'minAmount cannot be greater than maxAmount' });
/**
 * PUT /api/admin/limits/deposit/:method response
 * PUT /api/admin/limits/withdrawal/:method response
 */
export const updateMethodLimitsResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        method: z.string(),
        minAmount: z.number().int(),
        maxAmount: z.number().int(),
    }),
});
// =============================================================================
// STORED PROCEDURE RESULT
// =============================================================================
/**
 * update_payment_service_limits() result
 * SQL: SELECT * FROM update_payment_service_limits($1, $2, $3, $4)
 */
export const updatePaymentServiceLimitsResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    service_id: z.number().int().nullable(),
});
// =============================================================================
// ERROR RESPONSES
// =============================================================================
/**
 * Limits error response
 */
export const limitsErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
/**
 * Method not found error (404)
 */
export const methodNotFoundErrorSchema = z.object({
    success: z.literal(false),
    error: z.string().regex(/not found$/i),
});
//# sourceMappingURL=limits.js.map